/**
 * CraveTheory testimonials slider
 * - Prev / next + dots
 * - Crossfade quote + image
 * - Keyboard + reduced-motion aware
 */
(() => {
  const ROOT = '[data-ct-testimonials]';

  /** @param {HTMLElement} root */
  const init = (root) => {
    if (root.dataset.ctTestimonialsReady === 'true') return;
    root.dataset.ctTestimonialsReady = 'true';

    const slides = /** @type {HTMLElement[]} */ (
      Array.from(root.querySelectorAll('[data-ct-t-slide]'))
    );
    const images = /** @type {HTMLElement[]} */ (
      Array.from(root.querySelectorAll('[data-ct-t-image]'))
    );
    const dots = /** @type {HTMLButtonElement[]} */ (
      Array.from(root.querySelectorAll('[data-ct-t-dot]'))
    );
    const prev = root.querySelector('[data-ct-t-prev]');
    const next = root.querySelector('[data-ct-t-next]');
    const live = root.querySelector('[data-ct-t-live]');

    if (slides.length < 2) {
      root.classList.add('is-single');
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let index = slides.findIndex((s) => s.classList.contains('is-active'));
    if (index < 0) index = 0;
    let locked = false;

    /** @param {number} i */
    const clamp = (i) => {
      const n = slides.length;
      return ((i % n) + n) % n;
    };

    /** @param {number} nextIndex @param {'next' | 'prev' | 'dot'} [dir] */
    const goTo = (nextIndex, dir = 'dot') => {
      const target = clamp(nextIndex);
      if (target === index || locked) return;
      locked = true;

      const currentSlide = slides[index];
      const nextSlide = slides[target];
      const currentImage = images[index];
      const nextImage = images[target];

      if (currentSlide) {
        currentSlide.classList.remove('is-active', 'is-enter-left', 'is-enter-right');
        currentSlide.classList.add(dir === 'prev' ? 'is-exit-right' : 'is-exit-left');
      }
      if (nextSlide) {
        nextSlide.classList.remove('is-exit-left', 'is-exit-right');
        nextSlide.classList.add('is-active');
      }

      if (currentImage) currentImage.classList.remove('is-active');
      if (nextImage) nextImage.classList.add('is-active');

      dots.forEach((dot, i) => {
        const on = i === target;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });

      if (live instanceof HTMLElement) {
        const name = nextSlide?.dataset.author || '';
        live.textContent = name
          ? `Showing review from ${name}`
          : `Showing review ${target + 1} of ${slides.length}`;
      }

      const finish = () => {
        slides.forEach((s, i) => {
          s.classList.remove('is-exit-left', 'is-exit-right', 'is-enter-left', 'is-enter-right');
          s.classList.toggle('is-active', i === target);
        });
        index = target;
        locked = false;
      };

      if (reduceMotion) {
        finish();
        return;
      }

      window.setTimeout(finish, 480);
    };

    prev?.addEventListener('click', () => goTo(index - 1, 'prev'));
    next?.addEventListener('click', () => goTo(index + 1, 'next'));

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => goTo(i, i > index ? 'next' : 'prev'));
    });

    root.addEventListener('keydown', (event) => {
      if (!(event.target instanceof HTMLElement)) return;
      if (!root.contains(event.target)) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(index - 1, 'prev');
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(index + 1, 'next');
      }
    });

    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      if (!(block instanceof HTMLElement) || !root.contains(block)) return;
      const slide = block.closest('[data-ct-t-slide]');
      if (!(slide instanceof HTMLElement)) return;
      const i = slides.indexOf(slide);
      if (i >= 0) goTo(i, i > index ? 'next' : 'prev');
    });
  };

  const boot = () => {
    document.querySelectorAll(ROOT).forEach((el) => {
      if (el instanceof HTMLElement) {
        // Allow re-init after theme editor section reload
        if (el.dataset.ctTestimonialsReady === 'true' && !el.querySelector('[data-ct-t-slide]')) {
          delete el.dataset.ctTestimonialsReady;
        }
        init(el);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!(section instanceof HTMLElement)) {
      boot();
      return;
    }
    const root = section.matches(ROOT) ? section : section.querySelector(ROOT);
    if (root instanceof HTMLElement) {
      delete root.dataset.ctTestimonialsReady;
      init(root);
    }
  });

  window.addEventListener('pageshow', boot);
})();
